/**
 * Where the app is currently running.
 *
 * `preview` — Lovable preview / local dev
 * `staging`  — the staging deployment
 * `production` — the live site
 */
export type RuntimeEnvironment = 'production' | 'staging' | 'preview';

const PRODUCTION_HOSTS = ['wealthtimeline.ai', 'www.wealthtimeline.ai'];

export function detectRuntimeEnvironment(
  explicit?: string,
  hostname?: string,
): RuntimeEnvironment {
  const value = (explicit ?? '').trim().toLowerCase();
  if (value === 'production' || value === 'staging' || value === 'preview') return value;

  const host = (hostname ?? '').toLowerCase();
  if (!host) return 'preview';
  if (PRODUCTION_HOSTS.includes(host)) return 'production';
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.lovable.app') ||
    host.endsWith('.lovableproject.com')
  ) {
    return 'preview';
  }
  return 'staging';
}

const hostname = typeof window !== 'undefined' ? window.location?.hostname : undefined;

/** Fine-grained label used when recording analytics and error events. */
export const RUNTIME_ENVIRONMENT: RuntimeEnvironment = detectRuntimeEnvironment(
  import.meta.env.VITE_APP_ENV as string | undefined,
  hostname,
);

export const PUBLIC = {
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL as string,
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
  STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY as string,
  // Two-way flag kept for existing behaviour: anything that is not the live
  // site behaves like staging (debug panels, billing guards).
  APP_ENV: RUNTIME_ENVIRONMENT === 'production' ? 'production' : 'staging',
  BILLING_LIVE_ENABLED: import.meta.env.VITE_BILLING_LIVE_ENABLED === 'true',
};
